import { z } from "zod";
import { Uid } from "./docs.js";
import { AccountId } from "./ids.js";
import { MailPlanBulkOutput, MailPlanSample, MailSearchWarning } from "./mail-api.js";
import { QueryNode } from "./query.js";
/**
 * Mail rules (architecture §7): a query and actions over some of the caller's accounts. `native`
 * rules are compiled to Gmail filters / Outlook inbox rules and run by the provider; `app` rules
 * are what providers cannot express, run by the sync worker on new non-echo mail.
 */
export const RuleAction = z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("archive") }),
    z.object({ kind: z.literal("tag"), tag: z.string().min(1).max(100) }),
    z.object({ kind: z.literal("moveTo"), folder: z.string().min(1).max(100) }),
    z.object({ kind: z.literal("markRead") }),
    z.object({ kind: z.literal("star") }),
    z.object({ kind: z.literal("spam") }),
    z.object({ kind: z.literal("trash") }),
    z.object({ kind: z.literal("forward"), to: z.string().email() }),
]);
export const RuleMode = z.enum(["native", "app"]);
/** The provider rules (Gmail filter ids / Graph messageRule ids) one account holds for a rule. */
export const ProviderRef = z.object({ accountId: AccountId, ids: z.array(z.string().min(1)) });
const RuleFields = z.object({
    ownerUid: Uid,
    /** Set when the rule was scoped to a workspace (its accounts at the time); null for picked accounts. */
    workspaceId: z.string().nullable(),
    accountIds: z.array(AccountId).min(1),
    name: z.string().min(1).max(120),
    /** The query as typed (Gmail syntax); `match` is its parsed AST. */
    query: z.string().max(2000),
    match: QueryNode,
    actions: z.array(RuleAction).min(1).max(10),
    mode: RuleMode,
    providerRefs: z.array(ProviderRef).default([]),
    enabled: z.boolean(),
    /** When a person approved (or was present for) a destructive rule; app mode refuses to run without it. */
    approvedAt: z.date().nullable().default(null),
    createdAt: z.date(),
    updatedAt: z.date(),
    /** The last provider failure; a rule with one stays disabled until it is fixed. */
    lastError: z.string().nullable().default(null),
});
/**
 * Before WP-011, `mail.blockSender` wrote `{accountId, workspaceId, match, actions, providerRefs:
 * string[], enabled, createdAt}`. Reading such a document upgrades it: one account, its refs on
 * that account, a query rendered from the single `from:` match, and approval at creation (block
 * sender is destructive, so it only ran after approval or with presence).
 */
function upgradeLegacy(raw) {
    if (typeof raw !== "object" || raw === null)
        return raw;
    const doc = raw;
    if (doc["accountIds"] !== undefined || typeof doc["accountId"] !== "string")
        return raw;
    const { accountId, providerRefs, ...rest } = doc;
    const match = doc["match"];
    const refs = Array.isArray(providerRefs) ? providerRefs.filter((r) => typeof r === "string") : [];
    return {
        ...rest,
        accountIds: [accountId],
        providerRefs: refs.length === 0 ? [] : [{ accountId, ids: refs }],
        query: match?.kind === "field" && typeof match.field === "string" && typeof match.value === "string" ? `${match.field}:${match.value}` : "",
        updatedAt: doc["createdAt"],
        approvedAt: doc["createdAt"],
    };
}
/** `rules/{ruleId}` (architecture §8). Legacy block-sender documents are upgraded on read. */
export const RuleDoc = z.preprocess(upgradeLegacy, RuleFields);
/** The schema for writing (no legacy upgrade). */
export const RuleDocWrite = RuleFields;
// ── api ───────────────────────────────────────────────────────────────────────────────────────
/** Which accounts a rule covers: picked accounts, or a workspace's accounts (§7 scope). */
export const RuleScope = z.object({
    accountIds: z.array(AccountId).min(1).max(50).optional(),
    workspaceId: z.string().min(1).optional(),
});
export const RuleCreateInput = RuleScope.extend({
    name: z.string().min(1).max(120),
    query: z.string().min(1).max(2000),
    actions: z.array(RuleAction).min(1).max(10),
});
export const RuleUpdateInput = RuleCreateInput.extend({ ruleId: z.string().min(1) });
export const RuleIdInput = z.object({ ruleId: z.string().min(1) });
export const RuleSetEnabledInput = z.object({ ruleId: z.string().min(1), enabled: z.boolean() });
/** A rule as the api returns it. */
export const RuleView = RuleFields.extend({ id: z.string() });
export const RulePreviewInput = RuleScope.extend({
    query: z.string().min(1).max(2000),
    /** When given, the preview also says whether the rule would run natively. */
    actions: z.array(RuleAction).max(10).optional(),
});
export const RulePreviewOutput = z.object({
    /** Messages matching across the scope, counted up to `limit`. */
    matched: z.number().int().nonnegative(),
    /** True when there are more than `matched`. */
    more: z.boolean(),
    sample: z.array(MailPlanSample),
    warnings: z.array(MailSearchWarning),
    /** The accounts searched (the rule's scope). */
    accounts: z.array(z.object({ accountId: AccountId, address: z.string() })),
    /** How the rule would run with the given actions; null when none were given. */
    mode: RuleMode.nullable(),
});
/** What creating or changing a rule returns. */
export const RuleResult = z.object({
    ruleId: z.string(),
    mode: RuleMode,
    enabled: z.boolean(),
    providerRefs: z.array(ProviderRef),
    lastError: z.string().nullable(),
});
/** "Apply to existing mail": one bulk plan per action the plans can express (not forward). */
export const RulePlanExistingOutput = z.object({ plans: z.array(MailPlanBulkOutput), skipped: z.array(z.string()) });
//# sourceMappingURL=rules.js.map